// IAPP2-1744: SSD payment removed
// import { SsdDateOptionsEnum } from 'app/enums/form/form.enum';

export class PaymentDateUtil {
    /**
     * Gets the nth Wednesday of a given month
     * @param month - Month (0-11)
     * @param year - Full year
     * @param n - Which Wednesday (2, 3, or 4)
     */
    public static getNthWednesday(month: number, year: number, n: number): Date {
        const firstDayOfMonth = new Date(year, month, 1);
        let dayOfWeek = firstDayOfMonth.getDay();

        // Calculate days until first Wednesday (3 = Wednesday)
        let daysUntilFirstWednesday = (3 - dayOfWeek + 7) % 7;
        if (daysUntilFirstWednesday === 0 && dayOfWeek !== 3) {
            daysUntilFirstWednesday = 7;
        }

        // Calculate the nth Wednesday
        const nthWednesday = new Date(year, month, 1 + daysUntilFirstWednesday + (n - 1) * 7);

        return nthWednesday;
    }

    /**
     * Gets the first day of the next month from given date (or today if not provided)
     */
    public static getFirstDayOfNextMonth(fromDate?: Date): Date {
        const baseDate = fromDate ? new Date(fromDate) : new Date();
        const year = baseDate.getFullYear();
        const month = baseDate.getMonth();

        if (month === 11) {
            return new Date(year + 1, 0, 1);
        }
        return new Date(year, month + 1, 1);
    }

    /**
     * Gets the third day of the next month from given date (or today if not provided)
     */
    public static getThirdDayOfNextMonth(fromDate?: Date): Date {
        const baseDate = fromDate ? new Date(fromDate) : new Date();
        const year = baseDate.getFullYear();
        const month = baseDate.getMonth();

        if (month === 11) {
            return new Date(year + 1, 0, 3);
        }
        return new Date(year, month + 1, 3);
    }

    // IAPP2-1744: SSD payment removed
    // /**
    //  * Calculates the actual date for SSD payment options
    //  * @param option - SSD date option enum value
    //  * @param referenceDate - Reference date to calculate from (usually today or initial payment date)
    //  * @returns The calculated date
    //  */
    // public static calculateSsdDateOption(option: SsdDateOptionsEnum, referenceDate: Date): Date {
    //     const today = new Date(referenceDate);
    //     today.setHours(0, 0, 0, 0);

    //     const currentMonth = today.getMonth();
    //     const currentYear = today.getFullYear();
    //     const currentDay = today.getDate();

    //     let candidateThisMonth: Date;
    //     let candidateNextMonth: Date;

    //     switch (option) {
    //         case SsdDateOptionsEnum.FIRST_DAY:
    //             candidateThisMonth = new Date(currentYear, currentMonth, 1);
    //             candidateNextMonth = PaymentDateUtil.getFirstDayOfNextMonth(today);
    //             break;

    //         case SsdDateOptionsEnum.THIRD_DAY:
    //             candidateThisMonth = new Date(currentYear, currentMonth, 3);
    //             candidateNextMonth = PaymentDateUtil.getThirdDayOfNextMonth(today);
    //             break;

    //         case SsdDateOptionsEnum.SECOND_WEDNESDAY:
    //             candidateThisMonth = PaymentDateUtil.getNthWednesday(currentMonth, currentYear, 2);
    //             candidateNextMonth = PaymentDateUtil.getNthWednesday(
    //                 currentMonth === 11 ? 0 : currentMonth + 1,
    //                 currentMonth === 11 ? currentYear + 1 : currentYear,
    //                 2
    //             );
    //             break;

    //         case SsdDateOptionsEnum.THIRD_WEDNESDAY:
    //             candidateThisMonth = PaymentDateUtil.getNthWednesday(currentMonth, currentYear, 3);
    //             candidateNextMonth = PaymentDateUtil.getNthWednesday(
    //                 currentMonth === 11 ? 0 : currentMonth + 1,
    //                 currentMonth === 11 ? currentYear + 1 : currentYear,
    //                 3
    //             );
    //             break;

    //         case SsdDateOptionsEnum.FOURTH_WEDNESDAY:
    //             candidateThisMonth = PaymentDateUtil.getNthWednesday(currentMonth, currentYear, 4);
    //             candidateNextMonth = PaymentDateUtil.getNthWednesday(
    //                 currentMonth === 11 ? 0 : currentMonth + 1,
    //                 currentMonth === 11 ? currentYear + 1 : currentYear,
    //                 4
    //             );
    //             break;

    //         default:
    //             return today;
    //     }

    //     // If candidate date in this month has already passed or is today, use next month
    //     if (candidateThisMonth.getTime() <= today.getTime()) {
    //         return candidateNextMonth;
    //     }

    //     return candidateThisMonth;
    // }

    /**
     * Calculates the end of the billing cycle (paid-to date)
     * @param initialDate - The initial payment date
     * @returns Date representing end of first billing cycle (initial date + 1 month)
     */
    public static calculateEndOfBillingCycle(initialDate: Date): Date {
        const endDate = new Date(initialDate);
        const currentMonth = endDate.getMonth();

        if (currentMonth === 11) {
            endDate.setFullYear(endDate.getFullYear() + 1);
            endDate.setMonth(0);
        } else {
            endDate.setMonth(currentMonth + 1);
        }

        return endDate;
    }

    /**
     * Determines if double payment warning should be shown
     * @param initialDate - Initial payment date
     * @param recurringDate - First recurring payment date
     * @returns true if warning should be shown (recurring < paid_to_date)
     */
    public static shouldShowDoublePaymentWarning(initialDate: Date, recurringDate: Date): boolean {
        const paidToDate = PaymentDateUtil.calculateEndOfBillingCycle(initialDate);
        return recurringDate < paidToDate;
    }

    /**
     * Calculates the first occurrence of a recurring payment date
     * @param initialDate - Initial payment date
     * @param recurringDay - Day of month for recurring payment (1-28)
     * @returns The first recurring payment date (may cause double payment if before paid-to date)
     */
    public static calculateFirstRecurringDate(initialDate: Date, recurringDay: number): Date {
        const initialDay = initialDate.getDate();
        const paidToDate = PaymentDateUtil.calculateEndOfBillingCycle(initialDate);

        // If recurring day > initial day: use paid-to month (e.g., Dec 19 initial, day 20 → Jan 20)
        if (recurringDay > initialDay) {
            return new Date(paidToDate.getFullYear(), paidToDate.getMonth(), recurringDay);
        }

        // If recurring day <= initial day:
        // - If gap > 15 days: use month after paid-to (e.g., Dec 28 initial, day 1 → Feb 1)
        // - If gap <= 15 days: use paid-to month (e.g., Dec 28 initial, day 13 → Jan 13)
        const dayGap = initialDay - recurringDay;
        if (dayGap > 15) {
            const monthAfterPaidTo = paidToDate.getMonth() === 11 ? 0 : paidToDate.getMonth() + 1;
            const yearAfterPaidTo = paidToDate.getMonth() === 11 ? paidToDate.getFullYear() + 1 : paidToDate.getFullYear();
            return new Date(yearAfterPaidTo, monthAfterPaidTo, recurringDay);
        }

        // Gap < 15 days: use paid-to month
        return new Date(paidToDate.getFullYear(), paidToDate.getMonth(), recurringDay);
    }
}
